/**
 * What a line of markdown is, and how much of a half-written answer can be
 * shown without the reader watching it rewrite itself.
 *
 * The block parser in `message-parts` and the streaming reveal both have to
 * agree on when a run of pipes has become a table. Holding one definition here
 * is what keeps them from disagreeing for a frame.
 */

const FENCE_PATTERN = /^\s*(`{3,}|~{3,})/;
const TABLE_LINE_PATTERN = /^\s{0,3}\|/;
const SEPARATOR_CELL_PATTERN = /^:?-{3,}:?$/;
// A line holding nothing but the marker that opens a block. Until something
// follows it, `##` is literal text rather than a heading and `-` is an empty
// bullet, so both change shape as soon as the next character lands.
const BARE_BLOCK_MARKER_PATTERN = /^\s{0,3}(?:#{1,6}|[-*+]|\d{1,9}[.)]|>)\s*$/;
// Emphasis that has been opened with nothing after it yet. Streamdown closes
// a marker that already has content behind it, but a marker on its own is
// painted as the asterisk it looks like.
const TRAILING_EMPHASIS_OPENER_PATTERN = /(?:^|\s)([*_~`]+)$/;

export function getFenceMarker(line: string | undefined): string | null {
  const match = line?.match(FENCE_PATTERN);
  return match?.[1] ?? null;
}

export function fenceCloses(openMarker: string, line: string | undefined): boolean {
  const marker = getFenceMarker(line);
  if (!marker) return false;
  return marker[0] === openMarker[0] && marker.length >= openMarker.length;
}

export function nextFenceState(openFence: string | null, line: string): string | null {
  const marker = getFenceMarker(line);
  if (!marker) return openFence;
  if (openFence) {
    return fenceCloses(openFence, line) ? null : openFence;
  }
  return marker;
}

/** A closer still being typed: same tick as the opener, shorter than it. */
function isPartialFenceCloser(openFence: string, line: string): boolean {
  const match = line.match(/^\s{0,3}([`~]+)[ \t]*$/);
  if (!match) return false;
  const marker = match[1];
  // A mixed run such as `~ is code, not a closer being typed. Hold the line
  // back only when every character matches the opener and more ticks may come.
  return (
    marker[0] === openFence[0]
    && marker === marker[0].repeat(marker.length)
    && marker.length < openFence.length
  );
}

export function splitPipeRow(line: string): string[] {
  const trimmed = line.trim();
  const withoutOuterPipes = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let currentCell = '';
  let isEscaped = false;

  for (const char of withoutOuterPipes) {
    if (char === '|' && !isEscaped) {
      cells.push(cleanCell(currentCell));
      currentCell = '';
      continue;
    }

    currentCell += char;
    isEscaped = char === '\\' && !isEscaped;
  }

  cells.push(cleanCell(currentCell));
  return cells;
}

export function isPipeRow(line: string | undefined): boolean {
  if (!line) return false;
  return splitPipeRow(line).length >= 2;
}

/** A line that may be (or may become) a table row while it is still arriving. */
export function isTableLine(line: string | undefined): boolean {
  if (!line) return false;
  // A leading pipe is enough: the first cell of `| Ev` is not yet two cells,
  // but it is already a GFM table line. `isPipeRow` also accepts rows that
  // omit the outer pipes, which CommonMark tables allow.
  return TABLE_LINE_PATTERN.test(line) || isPipeRow(line);
}

export function isSeparatorRow(line: string | undefined): boolean {
  if (!line) return false;

  const cells = splitPipeRow(line);
  if (cells.length < 2) return false;

  return cells.every((cell) => SEPARATOR_CELL_PATTERN.test(cell.replace(/\s+/g, '')));
}

function cleanCell(cell: string): string {
  return cell.trim().replace(/\\\|/g, '|');
}

/**
 * How much of `content` renders as what it will finally mean.
 *
 * Markdown is decided by characters that have not arrived yet: a `|` is a
 * paragraph until a separator row turns the run into a table, and `##` is
 * literal text until a word follows it. Revealing those characters as they
 * land paints the syntax, then tears the paragraph out and builds the real
 * block in its place — which reads as the answer flickering and correcting
 * itself. Holding the unsettled tail back for a few frames removes the rewrite
 * without slowing the prose, which is the bulk of any answer and is settled
 * the moment it arrives.
 *
 * The cut is always the start of a line or of a marker run, never a position
 * that depends on what comes after it, so it can only move forward. Text on
 * screen is never taken back.
 */
export function settledMarkdownLength(content: string): number {
  if (!content) return 0;

  const lines = content.split('\n');
  const lineStarts: number[] = [];
  let offset = 0;
  for (const line of lines) {
    lineStarts.push(offset);
    offset += line.length + 1;
  }

  const lastIndex = lines.length - 1;
  const tail = lines[lastIndex];
  const tailStart = lineStarts[lastIndex];

  // Inside a fence the text is literal, so it means now what it will mean
  // later and there is nothing to wait for.
  let openFence: string | null = null;
  for (let index = 0; index < lastIndex; index += 1) {
    openFence = nextFenceState(openFence, lines[index]);
  }
  if (openFence) {
    // Literal text inside a fence already means what it will mean. The one
    // thing still in the air is a closer being typed: three ticks of a
    // four-tick closer are code until the fourth lands and they become the
    // fence, which would take them back off the page.
    return isPartialFenceCloser(openFence, tail) ? tailStart : content.length;
  }

  const tableRunStart = trailingTableRunStart(lines, lastIndex);
  if (tableRunStart !== null) {
    // Complete lines only: the tail is still being written either way.
    const settledRun = lines.slice(tableRunStart, lastIndex);
    if (!settledRun.some(isSeparatorRow)) {
      // No separator yet, so this is still a paragraph of pipes.
      return lineStarts[tableRunStart];
    }
    // A row is only a row once both its cells are there.
    if (tail !== '') return tailStart;
  }

  if (getFenceMarker(tail) || BARE_BLOCK_MARKER_PATTERN.test(tail)) {
    return tailStart;
  }

  const emphasisOpener = tail.match(TRAILING_EMPHASIS_OPENER_PATTERN);
  if (emphasisOpener) {
    return content.length - emphasisOpener[1].length;
  }

  return content.length;
}

/**
 * Where to stop this frame so that what is painted is a block, not a block
 * half-built.
 *
 * Holding the unsettled tail out of the target is only half of it: a reveal
 * that walks toward a settled target a few characters at a time walks straight
 * back through the states the hold was there to skip. So a construct is
 * revealed whole — the same bargain `getCitationAwareRevealLength` strikes for
 * a citation, which is shown all at once or not at all.
 */
export function settledRevealLength(
  target: string,
  revealed: number,
  wanted: number,
): number {
  if (wanted >= target.length) return target.length;
  // A target that is not settled itself belongs to a caller that has stopped
  // holding anything back — the turn ended, or the lawyer pressed stop — and
  // waiting for a boundary that will never arrive would strand the answer.
  if (settledMarkdownLength(target) !== target.length) return wanted;

  const boundary = settledMarkdownLength(target.slice(0, wanted));
  if (boundary > revealed) return boundary;

  // The whole budget landed inside one construct. Take all of it rather than
  // stand still, stopping at the first line boundary that reads correctly.
  let index = Math.max(revealed, wanted);
  while (index < target.length) {
    const newline = target.indexOf('\n', index);
    const candidate = newline === -1 ? target.length : newline + 1;
    if (settledMarkdownLength(target.slice(0, candidate)) === candidate) {
      return candidate;
    }
    index = candidate;
  }

  return target.length;
}

/** Index of the first line in the run of table lines the content ends on. */
function trailingTableRunStart(lines: string[], lastIndex: number): number | null {
  let runStart: number | null = null;

  for (let index = lastIndex; index >= 0; index -= 1) {
    // Nothing written on the line yet says nothing about the run above it.
    if (index === lastIndex && lines[index] === '') continue;
    if (!isTableLine(lines[index])) break;
    runStart = index;
  }

  return runStart;
}
